// src/utils/charting.utils.ts

import {
  ToothCondition,
  GeneralToothCondition,
  SurfaceCode,
} from '@dataTypes/chartingFeature.types'

const generalMap: Record<string, GeneralToothCondition> = {
  cavity: 'caries',
  caries: 'caries',
  decay: 'caries',
  filling: 'filling',
  restoration: 'filling',
  implant: 'implant',
  implants: 'implant',
  crown: 'crown',
  crowns: 'crown',
  veneer: 'veneer',
  veneers: 'veneer',
  bridge: 'bridge',
  bridges: 'bridge',
  abutment: 'abutment',
  'bridge left bridge': 'bridge_left_bridge',
  'bridge left abutment': 'bridge_left_abutment',
  'bridge right bridge': 'bridge_right_bridge',
  'bridge right abutment': 'bridge_right_abutment',
  'bridge right-left bridge': 'bridge_right_left_bridge',
  'bridge right-left abutment': 'bridge_right_left_abutment',
  fracture: 'fracture',
  'fracture mesiobuccal': 'fracture_mesiobuccal',
  'fracture distobuccal': 'fracture_distobuccal',
  'fracture mesiolingual': 'fracture_mesiolingual',
  'fracture distolingual': 'fracture_distolingual',
  'fracture mesiopalatal': 'fracture_mesiopalatal',
  'fracture distopalatal': 'fracture_distopalatal',
  'fracture vertical_root': 'fracture_vertical_root',
  'fracture horizontal_root': 'fracture_horizontal_root',
  fracture_mesiobuccal: 'fracture_mesiobuccal',
  fracture_distobuccal: 'fracture_distobuccal',
  fracture_mesiolingual: 'fracture_mesiolingual',
  fracture_distolingual: 'fracture_distolingual',
  fracture_mesiopalatal: 'fracture_mesiopalatal',
  fracture_distopalatal: 'fracture_distopalatal',
  fracture_vertical_root: 'fracture_vertical_root',
  fracture_horizontal_root: 'fracture_horizontal_root',
  crack: 'crack',
  'crack vertical_crown_buccal': 'crack_vertical_crown_buccal',
  'crack vertical_crown_lingual': 'crack_vertical_crown_lingual',
  'crack vertical_crown_palatal': 'crack_vertical_crown_palatal',
  'crack vertical_crown_occlusal': 'crack_vertical_crown_occlusal',
  'crack horizontal_crown_buccal': 'crack_horizontal_crown_buccal',
  'crack horizontal_crown_lingual': 'crack_horizontal_crown_lingual',
  'crack horizontal_crown_palatal': 'crack_horizontal_crown_palatal',
  'crack horizontal_crown_occlusal': 'crack_horizontal_crown_occlusal',
  wear: 'attrition',
  attrition: 'attrition',
  sealant: 'sealants',
  sealants: 'sealants',
  inlay: 'inlay',
  onlay: 'onlay',
  abrasion: 'abrasion',
  erosion: 'erosion',
  impacted: 'impacted',
  missing: 'missing',
  present: 'present',
  furcation: 'furcation',
  bleeding: 'bleeding',
  'root canal': 'root_canal',
  rootcanal: 'root_canal',
  'partial pulp': 'pulpotomy',
  pulpotomy: 'pulpotomy',
  pulpectomy: 'pulpectomy',
  apicoetomy: 'apicoectomy',
  apicoectomy: 'apicoectomy',
  periapical: 'periapical',
  periradicular: 'periradicular',
  'periradicular slight': 'periradicular_slight',
  'periradicular severe': 'periradicular_severe',
  'periradicular moderate': 'periradicular_moderate',
  drifted: 'drifted',
  supernumerary: 'supernumerary',
  'sinus tract': 'sinus_tract',
  recession: 'recession',
  'retained primary': 'retainer',
  retainer: 'retainer',
}

const surfaceCodes: SurfaceCode[] = [
  'm', // Mesial
  'd', // Distal
  'b', // Buccal
  'o', // Occlusal
  'p', // Palatal
  'i', // Incisal
  'l', // Lingual
  'mb', // Mesiobuccal
  'db', // Distobuccal
  'mp', // Mesiopalatal
  'dp', // Distopalatal
  'cb', // Cervical Buccal
  'cp', // Cervical Palatal
  'bc', // Buccal Cusp
  'bs', // Buccal Surface
  'pc', // Palatal Cusp
  'ps', // Palatal Surface
  'ls', // Lingual Surface
  'lc', // Lingual Cusp
  'cl', // Cervical Lingual
  'dlc', // Disto Lingual Cusp
  'mlc', // Mesio Lingual Cusp
  'mifp',
  'difp',
  'mifl',
  'difl',
  'erosion_buccal', // Erosion Buccal
  'erosion_palatal', // Erosion Palatal
  'erosion_lingual', // Erosion Lingual
  'inlay_m', // Inlay Mesial
  'inlay_d', // Inlay Distal
  'inlay_o', // Inlay Occlusal
  'onlay_m', // Onlay Mesial
  'onlay_d', // Onlay Distal
  'onlay_o', // Onlay Occlusal
  'onlay_b', // Onlay Buccal
  'onlay_p', // Onlay Palatal
  'drifted_b', // Drifted Buccal
  'drifted_m', // Drifted Mesial
  'drifted_d', // Drifted Distal
  'drifted_p', // Drifted Palatal
  'drifted_l', // Drifted Lingual
  'filling_ceramic', // Filling Ceramic Material
  'filling_composite', // Filling Composite Material
  'filling_gold', // Filling Gold Material
  'filling_amalgam', // Filling Amalgam Material
  'filling_temporary', // Filling Temporary Material
  'filling_m', // Filling Mesial
  'filling_d', // Filling Distal
  'filling_o', // Filling Occlusal
  'filling_i', // Filling Incisal
  'filling_b', // Filling Buccal
  'filling_p', // Filling Palatal
]

export const normalizeCondition = (label: string): ToothCondition => {
  const lower = label.toLowerCase().trim()

  if (generalMap[lower]) return generalMap[lower]
  if (surfaceCodes.includes(lower as SurfaceCode)) return lower as SurfaceCode

  return lower as ToothCondition
}

export const parseConditionMap = (
  toothSurface: string,
  condition: string,
): Record<number, ToothCondition[]> => {
  const surfaceEntries = toothSurface.split(';').map((s) => s.trim())
  const conditionEntries = condition.split(';').map((entry) =>
    entry
      .trim()
      .split(',')
      .map((c) => normalizeCondition(c.trim())),
  )

  const map: Record<number, ToothCondition[]> = {}

  for (let i = 0; i < surfaceEntries.length; i++) {
    const [toothStr] = surfaceEntries[i].split('/')
    const tooth = parseInt(toothStr, 10)

    if (!isNaN(tooth)) {
      if (!map[tooth]) map[tooth] = []
      map[tooth].push(...(conditionEntries[i] || []))
    }
  }

  return map
}

export const mergeConditionMaps = (
  base: Record<number, ToothCondition[]>,
  incoming: Record<number, ToothCondition[]>,
): Record<number, ToothCondition[]> => {
  const merged: Record<number, ToothCondition[]> = { ...base }

  for (const [toothStr, newConds] of Object.entries(incoming)) {
    const tooth = Number(toothStr)
    const existing = merged[tooth] || []
    merged[tooth] = Array.from(new Set([...existing, ...newConds]))
  }

  return merged
}

export const replaceUnderscoreInSurfaceCodes = (
  input: Record<number, ToothCondition[]>,
): Record<number, ToothCondition[]> => {
  const surfaceCodeSet = new Set(surfaceCodes)
  const output: Record<number, ToothCondition[]> = {}

  for (const tooth in input) {
    output[tooth] = input[tooth].map((code) =>
      Array.from(surfaceCodeSet).some((sc) => {
        if (sc.includes('_')) {
          const scParts = sc.split('_')
          if (scParts.length > 1) {
            const codeParts = code.split('_')
            // Check if the second element of codeParts matches scParts[1] and scParts[1] is in surfaceCodes (without "_")
            return (
              codeParts.length > 1 &&
              surfaceCodes.includes(scParts[1] as SurfaceCode) &&
              codeParts[1] === scParts[1]
            )
          }
          return false
        }
        return code.endsWith(`_${sc}`) || code === sc
      })
        ? code.replaceAll('_', '-')
        : code,
    ) as ToothCondition[]
  }

  return output
}

export const replaceDashInSurfaceCodes = (
  input: Record<number, ToothCondition[]>,
): Record<number, ToothCondition[]> => {
  const output: Record<number, ToothCondition[]> = {}

  for (const tooth in input) {
    output[tooth] = input[tooth].map((code) =>
      code.replaceAll('-', '_'),
    ) as ToothCondition[]
  }
  return output
}
